<template>
  <div class="s-file-viewer">
    <p v-if="label" class="label">{{ label }}</p>
    <slot
      name="files"
      v-bind="{
        files,
        checkFileAction,
        getFileIndex,
        formatFileSize
      }"
    >
      <div v-if="files.length" class="file-viewer">
        <div v-if="files.length" :class="`files__list ${displayMode === DisplayMode.IMAGE_ONLY ? 'files__list--row' : ''}`">
          <div v-for="(file, index) in files" :key="`${file.name}-${index}`" class="file">
            <div class="file__content">
              <FilePreview
                :file="file"
                :sizes="{
                  height: imageHeight,
                  width: imageWidth,
                }"
                @click="checkFileAction(file, getFileIndex(file))"
              />
              <div v-if="displayMode === DisplayMode.IMAGE_WITH_TEXT" class="file__description">
                <div class="file__description-name">{{ file.name }}</div>
                <div class="file__description-size">{{ formatFileSize(file.size) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </slot>

    <FileViewer
      v-if="isUploaderModalDialogVisible"
      :label="viewerTitle"
      :files="filteredFiles"
      :file-index="fileIndex"
      :image-formats="imageFormats"
      :removable="removable"
      @hide-dialog="onHideModalDialog"
      @file-index-changed="onFileIndexChange"
    />
    <FilePlugSprite />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUpdated, onBeforeUnmount } from 'vue'

import '@splidejs/vue-splide/css'
import FileViewer from '@components/file/FileViewer.vue'
import FilePreview from '@components/file/FilePreview.vue'
import FilePlugSprite from '@components/file/FilePlugSprite.vue'
import { formatFileSize } from './helper'
import { isRemoteFile, isLocalFile } from '@/types/uploader'
import { DisplayMode, UploaderFile } from '@/types/uploader'
import type { SplideComponentInstance } from './types'

const files = defineModel('files', { type: Array<UploaderFile>, required: true })

defineProps({
  displayMode: {
    type: String as () => DisplayMode,
    default: DisplayMode.IMAGE_WITH_TEXT
  },
  imageWidth: {
    type: Number,
    default: 48,
  },
  imageHeight: {
    type: Number,
    default: 48,
  },
  description: {
    type: String,
    default: ''
  },
  viewerTitle: {
    type: String,
    default: 'Файл'
  },
  label: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
  removable: {
    type: Boolean,
    default: true
  }
  // accept: {
  //   type: String,
  //   default: '.jpeg,.jpg,.png,.pdf',
  // },
  // multiple: {
  //   type: Boolean,
  //   default: false
  // },
})

const emit = defineEmits<{
  'file-click': [{ file: UploaderFile, index: number }]
}>()

const imageFormats = ['apng', 'gif', 'ico', 'cur', 'jpg', 'jpeg', 'pjpeg', 'pjp', 'png', 'svg', 'webp', 'bmp']

const fileIndex = ref(0)
const isDesktopPreview = ref(true)
const isLeftArrowVisible = ref(false)
const isRightArrowVisible = ref(false)
const isUploaderModalDialogVisible = ref(false)

const fileInput = ref<HTMLInputElement | null>(null)
const carousel = ref<SplideComponentInstance | null>(null)

const filteredFiles = computed<UploaderFile[]>(() => {
  return files.value
})

watch(files, (newFiles) => {
  if (!fileInput.value) {
    return
  }

  const fileItems = newFiles.filter((file) => isLocalFile(file)) as File[]

  if (fileItems.length) {
    fileInput.value.files = filesToFileList(fileItems)
  } else {
    fileInput.value.value = ''
  }
})

const getFileExtension = (filename: string) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

const filesToFileList = (files: File[]) => {
  const dt = new DataTransfer()
  for (const file of files) {
    dt.items.add(file)
  }
  return dt.files
}

const checkFileAction = (file: UploaderFile, index: number) => {
  if (imageFormats.includes(getFileExtension(file.name)) || getFileExtension(file.name) === 'pdf'){
    onFileClick(file, index)
  } else {
    onDownloadClick(file)
  }
}

const getFileIndex = (file: UploaderFile): number => {
  return filteredFiles.value.findIndex(({ name }) => name === file.name)
}

const onFileIndexChange = (index: number) => {
  fileIndex.value = index
}

const onHideModalDialog = () => {
  isUploaderModalDialogVisible.value = false
}

const onFileClick = (file: UploaderFile, index: number) => {
  emit('file-click', { file, index })
  fileIndex.value = index
  isUploaderModalDialogVisible.value = true
}

const onDownloadClick = (file: UploaderFile) => {
  try {
    const anchor = document.createElement('a')

    anchor.href = isRemoteFile(file) ? file.link : URL.createObjectURL(file)
    anchor.download = file.name

    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } catch (error: any) {
    console.warn(error?.message ?? error)
  }
}

const onResize = () => {
  isDesktopPreview.value = window.innerWidth < 744
}

const setArrowsVisibility = () => {
  if (carousel.value) {
    isRightArrowVisible.value = carousel.value.splide.index + 7 < carousel.value.splide.length
    isLeftArrowVisible.value = carousel.value.splide.index > 0
  }
}


onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
})

onUpdated(() => {
  setArrowsVisibility()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins';

.s-file-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;

  &-input {
    &__wrap {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
      cursor: pointer;

      &--icon {
        flex: 0 0 auto;
        margin-right: 12px;
      }
    }
  }

  .file-viewer {
    display: flex;
    position: relative;
    margin-bottom: 12px;
    width: 100%;
    overflow: hidden;

    .file {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      &__description {
        &-name {
          color: var(--color-text-primary);
          font-size: 16px;
          line-height: 22px;
        }
        &-size {
          color: var(--color-text-secondary);
          font-size: 14px;
          line-height: 20px;
        }
      }
      &__content {
        display: flex;
        flex-direction: row;
        gap: 12px;
        align-items: center;
      }
    }

    &__arrow {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      padding-bottom: 24px;

      &--left {
        width: 52px;
        margin-right: 12px;
        align-items: flex-start;
      }

      &--right {
        width: 52px;
        margin-left: 12px;
        align-items: flex-end;
      }

      .arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 44px;
        height: 44px;
        border-radius: 100px;
        background-color: var(--color-bg-secondary);
        border: 1px solid var(--stroke-cards);
        cursor: pointer;

        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
      }
    }

    &__files {
      width: 100%;
      position: relative;
      overflow: scroll;

      &::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;

      &--file {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 8px;

        &:hover {
          .file-view {
            cursor: default;

            &--hover {
              display: flex;
              position: absolute;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;

              .eye-icon {
                cursor: pointer;
              }
            }
          }

          @media (max-width: 743px) {
            .file-view {
              cursor: pointer;
              &--hover {
                display: none;
              }
            }
          }
        }
      }
    }

    @media (max-width: 743px) {
      &__arrow {
        display: none;
      }

      &__files {
        display: flex;
      }
    }
  }

  @media (max-width: 743px) {
    width: 100%;
  }

  .label {
    font-size: 18px;
    line-height: 1.3;
    margin-bottom: 18px;
  }

  .file-input {
    display: none;
  }
}

.files {
  &__list{
    &--row{
      display: flex;
      flex-direction: row;
      gap: 8px;
    }
  }
}
</style>
